import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "input",
    "successMessage",
    "errorMessage",
    "submit",
    "loading",
    "rule"
  ]

  static values = {
    url: String,
    minLength: Number,
    maxLength: Number,
    format: String,
    takenText: { type: String, default: "Username is taken" }
  }

  connect() {
    this.timeout = null
  }

  check() {
    clearTimeout(this.timeout)
    const username = this.inputTarget.value.trim()
    const rules = this.validateUsername(username)

    this.updateRules(rules)
    this.clearMessage()
    this.disableSubmit()

    if (!Object.values(rules).every(Boolean)) return

    this.timeout = setTimeout(() => {
      this.performCheck(username)
    }, 500)
  }

  validateUsername(username) {
    const format = new RegExp(this.formatValue)

    return {
      length: username.length >= this.minLengthValue && username.length <= this.maxLengthValue,
      characters: format.test(username)
    }
  }

  updateRules(rules) {
    this.ruleTargets.forEach((ruleTarget) => {
      const valid = rules[ruleTarget.dataset.rule] === true

      ruleTarget.classList.toggle("text-green-600", valid)
      ruleTarget.classList.toggle("text-gray-500", !valid)

      const icon = ruleTarget.querySelector("[data-username-rule-icon]")
      if (icon) {
        icon.classList.toggle("text-green-600", valid)
        icon.classList.toggle("text-gray-500", !valid)
      }
    })
  }

  async performCheck(username) {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden")
    }

    try {
      const response = await fetch(`${this.urlValue}?username=${encodeURIComponent(username)}`, {
        headers: {
          "Accept": "application/json"
        }
      })
      
      if (this.inputTarget.value.trim() !== username) return

      const data = await response.json()

      if (this.inputTarget.value.trim() === username) {
        this.updateUI(response.ok && data.available, data.error_message)
      }
    } catch (error) {
      console.error("Error checking username:", error)
    } finally {
      if (this.hasLoadingTarget) {
        this.loadingTarget.classList.add("hidden")
      }
    }
  }

  updateUI(isAvailable, errorMessage) {
    if (isAvailable) {
      this.successMessageTarget.classList.remove("hidden")
      this.errorMessageTarget.classList.add("hidden")
      this.inputTarget.classList.remove("border-red-500", "focus:ring-red-500")
      this.inputTarget.classList.add("border-green-500", "focus:ring-green-500")
      this.enableSubmit()
    } else {
      this.successMessageTarget.classList.add("hidden")
      this.errorMessageTarget.classList.remove("hidden")
      
      const span = this.errorMessageTarget.querySelector("span")
      if (span) {
        span.textContent = errorMessage || this.takenTextValue
      } else {
        this.errorMessageTarget.textContent = errorMessage || this.takenTextValue
      }

      this.inputTarget.classList.remove("border-green-500", "focus:ring-green-500")
      this.inputTarget.classList.add("border-red-500", "focus:ring-red-500")
      this.disableSubmit()
    }
  }

  clearMessage() {
    this.successMessageTarget.classList.add("hidden")
    this.errorMessageTarget.classList.add("hidden")
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden")
    }
    this.inputTarget.classList.remove("border-red-500", "border-green-500", "focus:ring-red-500", "focus:ring-green-500")
  }

  disableSubmit() {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = true
      this.submitTarget.classList.add("opacity-50", "cursor-not-allowed")
    }
  }

  enableSubmit() {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = false
      this.submitTarget.classList.remove("opacity-50", "cursor-not-allowed")
    }
  }
}
